import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { buildErrorMessage } from 'utils'
import { ApiError } from 'utils/error'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { scheduleKeys } from './scheduleKeys'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export interface Variables {
  campaignId: number
}

export const generateCampaignContent = async ({ campaignId }: Variables) => {
  const res = await sendToApi<void>(`/schedules/${campaignId}/generate_content`, {
    method: 'POST',
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }
    const plainStringErrors = Array.isArray(res.data?.errors) && typeof res.data.errors[0] === 'string'

    const message =
      (plainStringErrors ? res.data.errors.join('. ') : buildErrorMessage(res.data.errors)) ||
      'An error occurred! Our team has been notified and we are looking into it.'

    throw plainStringErrors && res.data?.error_code ? new ApiError(message, res.data.error_code) : new Error(message)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<void, Error, Variables>, 'mutationFn'> {}

export const useGenerateCampaignContent = (options?: Options) => {
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const calendarV2 = useFlipperFlag('calendar_v2')

  return useMutation<void, Error, Variables>(generateCampaignContent, {
    ...options,
    onSuccess: (data, vars, ctx) => {
      if (calendarV2) {
        queryClient.invalidateQueries(filesKeys.partialCalendar(workspaceId))
        queryClient.invalidateQueries(scheduleKeys.pending(workspaceId))
        queryClient.invalidateQueries(scheduleKeys.activeBatch(vars.campaignId))
      }
      options?.onSuccess?.(data, vars, ctx)
    },
  })
}
